import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useToast } from "react-native-toast-notifications";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { stockModel } from "../../../models/reportsModel";
import { bulkUpsertStockRemarks } from "../../../services/stockRemarksService";
import { useTranslation } from "../../../utils/useTranslation";

interface BulkStockRemarksModalProps {
  visible: boolean;
  onClose: () => void;
  stockList: Array<stockModel>;
  updatedBy?: string;
  onSaved?: () => void;
}

// One row per unique itemCode+whsCode — the same item can appear many
// times in stockReportList (once per warehouse/state), so this is the same
// dedupe key used everywhere else in this screen (see
// getOrderReservedLookupKey in StocksReportScreen).
interface DedupedStockRow {
  key: string;
  itemCode: string;
  whsCode: string;
  itemName: string;
  location: string;
}

const BulkStockRemarksModal: React.FC<BulkStockRemarksModalProps> = ({
  visible,
  onClose,
  stockList,
  updatedBy,
  onSaved,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const toast = useToast();
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const dedupedRows: DedupedStockRow[] = useMemo(() => {
    const seen = new Map<string, DedupedStockRow>();
    stockList.forEach((stock: any) => {
      const itemCode =
        typeof stock.ItemCode === "string"
          ? stock.ItemCode
          : stock.ItemCode?.[0];
      const itemName =
        typeof stock.ItemName === "string"
          ? stock.ItemName
          : stock.ItemName?.[0];
      const whsCode = stock.Whscode || stock.WhsCode || "";
      const location =
        typeof stock.Location === "string"
          ? stock.Location
          : stock.Location?.[0];

      if (!itemCode || !whsCode) return; // can't uniquely key this row — skip it

      const key = `${itemCode}_${whsCode}`;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          itemCode,
          whsCode,
          itemName: itemName || "",
          location: location || "",
        });
      }
    });
    return Array.from(seen.values());
  }, [stockList]);

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return dedupedRows;
    const q = searchText.trim().toLowerCase();
    return dedupedRows.filter(
      (row) =>
        row.itemCode.toLowerCase().includes(q) ||
        row.itemName.toLowerCase().includes(q) ||
        row.location.toLowerCase().includes(q),
    );
  }, [dedupedRows, searchText]);

  const toggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const allFilteredSelected = filteredRows.every((row) =>
      selectedKeys.has(row.key),
    );
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredRows.forEach((row) => next.delete(row.key));
      } else {
        filteredRows.forEach((row) => next.add(row.key));
      }
      return next;
    });
  };

  const reset = () => {
    setSearchText("");
    setSelectedKeys(new Set());
    setRemarks("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedKeys.size === 0) {
      toast.show(t("selectAtLeastOneItem") || "Select at least one item", {
        type: "danger",
      });
      return;
    }
    if (!remarks.trim()) {
      toast.show(t("enterRemarks") || "Enter a remark", { type: "danger" });
      return;
    }

    setSaving(true);
    try {
      const rowsByKey = new Map(dedupedRows.map((r) => [r.key, r]));
      const items = Array.from(selectedKeys)
        .map((key) => rowsByKey.get(key))
        .filter(Boolean)
        .map((row) => ({
          itemCode: row!.itemCode,
          whsCode: row!.whsCode,
          location: row!.location,
          itemName: row!.itemName,
          remarks: remarks.trim(),
        }));

      await bulkUpsertStockRemarks(items, updatedBy);

      toast.show(
        t("remarksSavedFor", { count: items.length }) ||
          `Remarks saved for ${items.length} item(s)`,
        { type: "success" },
      );
      onSaved?.();
      handleClose();
    } catch (error) {
      console.error("Error saving bulk stock remarks:", error);
      toast.show(t("remarksSaveFailed") || "Failed to save remarks", {
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <View style={styles.overlay}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {t("addRemarksToItems") || "Add Remarks to Items"}
          </Text>
          <TouchableOpacity onPress={handleClose}>
            <Ionicons name="close" size={22} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        <Text style={styles.helperText}>
          {t("bulkRemarksHelper") ||
            "Select the items below, then write one remark to apply to all of them. This replaces any existing remark on each item."}
        </Text>

        <TextInput
          style={styles.searchInput}
          placeholder={
            t("searchByCodeNameLocation") || "Search by code, name, or location"
          }
          placeholderTextColor={colors.textSecondary}
          value={searchText}
          onChangeText={setSearchText}
        />

        <View style={styles.selectAllRow}>
          <TouchableOpacity
            style={styles.selectAllBtn}
            onPress={toggleSelectAllFiltered}
          >
            <Text style={styles.selectAllText}>
              {filteredRows.length > 0 &&
              filteredRows.every((r) => selectedKeys.has(r.key))
                ? t("deselectAll") || "Deselect All"
                : t("selectAllVisible") || "Select All Visible"}
            </Text>
          </TouchableOpacity>
          <Text style={styles.selectedCount}>
            {selectedKeys.size} {t("selected") || "selected"}
          </Text>
        </View>

        <ScrollView style={styles.itemList} nestedScrollEnabled>
          {filteredRows.length === 0 ? (
            <Text style={styles.emptyText}>
              {t("noItemsFound") || "No items found"}
            </Text>
          ) : (
            filteredRows.map((row) => {
              const isChecked = selectedKeys.has(row.key);
              return (
                <TouchableOpacity
                  key={row.key}
                  style={styles.itemRow}
                  onPress={() => toggleRow(row.key)}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.checkbox,
                      isChecked && styles.checkboxChecked,
                    ]}
                  >
                    {isChecked && (
                      <Ionicons name="checkmark" size={14} color="#fff" />
                    )}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.itemName} numberOfLines={1}>
                      {row.itemName}
                    </Text>
                    <Text style={styles.itemMeta}>
                      #{row.itemCode} · {row.location}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </ScrollView>

        <Text style={styles.remarksLabel}>{t("remark") || "Remark"}</Text>
        <TextInput
          style={styles.remarksInput}
          placeholder={
            t("enterRemarkForSelectedItems") ||
            "Enter a remark for the selected items..."
          }
          placeholderTextColor={colors.textSecondary}
          value={remarks}
          onChangeText={setRemarks}
          multiline
          numberOfLines={3}
        />

        <TouchableOpacity
          style={[styles.submitButton, saving && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={saving}
        >
          <Text style={styles.submitButtonText}>
            {saving
              ? t("saving") || "Saving..."
              : `${t("saveRemarks") || "Save Remarks"} (${selectedKeys.size})`}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default BulkStockRemarksModal;

const createStyles = (colors: any) =>
  StyleSheet.create({
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(15, 23, 42, 0.4)",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
      padding: 20,
    },
    card: {
      width: "100%",
      maxWidth: 560,
      maxHeight: "85%",
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8,
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 12,
      lineHeight: 18,
    },
    searchInput: {
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 8,
      fontSize: 14,
      color: colors.text,
      marginBottom: 10,
    },
    selectAllRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6,
    },
    selectAllBtn: {
      paddingVertical: 4,
    },
    selectAllText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.primary,
    },
    selectedCount: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    itemList: {
      maxHeight: 260,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      marginBottom: 14,
    },
    itemRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.surfaceSubtle,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 1.5,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 10,
    },
    checkboxChecked: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    itemName: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
    itemMeta: {
      fontSize: 11,
      color: colors.textSecondary,
      marginTop: 2,
    },
    emptyText: {
      textAlign: "center",
      color: colors.textSecondary,
      paddingVertical: 24,
      fontSize: 13,
    },
    remarksLabel: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 6,
    },
    remarksInput: {
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 10,
      fontSize: 14,
      color: colors.text,
      minHeight: 80,
      textAlignVertical: "top",
      marginBottom: 14,
    },
    submitButton: {
      backgroundColor: colors.primary,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: "center",
    },
    submitButtonDisabled: {
      opacity: 0.6,
    },
    submitButtonText: {
      color: "#fff",
      fontSize: 14,
      fontWeight: "700",
    },
  });
